import type { Editor } from "@tiptap/core";

/**
 * Attribute marking a control that may take real focus without ending the
 * inline text-edit session. The session commits on the editor's blur, so a
 * toolbar control an author has to type into (the font-size box, the colour
 * popover) would otherwise close the session the moment it is clicked.
 */
export const TEXT_SESSION_HOLD_ATTRIBUTE = "data-fp-text-session-hold";

/** Props marking a control as holding the session open, spread onto it. */
export const TEXT_SESSION_HOLD_PROPS: Record<string, string> = {
  [TEXT_SESSION_HOLD_ATTRIBUTE]: "true",
};

/**
 * Whether focus moving to an element leaves the text session open.
 * @param target - The element focus is moving to, or null
 * @returns True when the element holds the session open
 */
export function holdsTextSession(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(`[${TEXT_SESSION_HOLD_ATTRIBUTE}]`) !== null
  );
}

/**
 * Whether focus leaving a toolbar control lands somewhere the session survives:
 * back in the edit surface, or on another control holding it open.
 * @param editor - The open session's TipTap instance
 * @param target - The element focus is moving to, or null
 * @returns True when the session should stay open
 */
export function keepsTextSession(
  editor: Editor,
  target: EventTarget | null,
): boolean {
  if (holdsTextSession(target)) {
    return true;
  }
  return target instanceof Node && editor.view.dom.contains(target);
}
